import {
CheckCircle,Refresh,
School,
Search,Star
} from '@mui/icons-material';
import {
Alert,Avatar,Box,
Button,Card,
CardContent,Chip,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,Divider,InputAdornment,LinearProgress,Pagination,Tab,Tabs,TextField,Typography
} from '@mui/material';
import React,{ useEffect,useRef,useState } from 'react';
import AudioPlayer from '../../../components/AudioPlayer';
import { useAllahNamesProgress } from '../hooks/useAllahNamesProgress';
import AyahViewer from './AyahViewer';

interface AllahNamesProgressProps {
  token: string | null;
  userId?: string;
}

const AllahNamesProgress: React.FC<AllahNamesProgressProps> = ({ token, userId }) => {
  const {
    allahNames,
    filteredNames,
    progress,
    currentName,
    isLoading,
    error,
    loadAllahNames,
    isSearching,
    searchQuery,
    searchNames,
    markAsLearned,
    unmarkAsLearned,
    markAsViewed,
    getNextName,
    getRandomName
  } = useAllahNamesProgress(token, userId);

  const [selectedTab, setSelectedTab] = useState(0);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedName = allahNames.find(name => name.id === selectedId) || null;
  const [currentPage, setCurrentPage] = useState(1);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const itemsPerPage = 10;

  // Состояние для просмотра аята
  const [showAyahDialog, setShowAyahDialog] = useState(false);
  const [selectedAyah, setSelectedAyah] = useState<{surah: number, ayah_start: number, ayah_end?: number} | null>(null);

  // Сбрасываем страницу при изменении поискового запроса
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleNameClick = (name: any) => {
    setSelectedId(name.id);
    setShowNameDialog(true);
    markAsViewed(name.id);
  };

  const handleAyahClick = (ref: any) => {
    setSelectedAyah({
      surah: ref.surah,
      ayah_start: ref.ayah_start,
      ayah_end: ref.ayah_end
    });
    setShowAyahDialog(true);
  };

  const handleBackToName = () => {
    setShowAyahDialog(false);
    setShowNameDialog(true);
  };

  const handleLearnName = async (nameId: string) => {
    if (await markAsLearned(nameId)) setShowNameDialog(false);
  };

  const handleNextName = () => {
    const nextName = getNextName(selectedName);
    if (nextName) {
      setSelectedId(nextName.id);
      markAsViewed(nextName.id);
    }
  };

  const handleRandomName = () => {
    const randomName = getRandomName();
    if (randomName) {
      setSelectedId(randomName.id);
      setShowNameDialog(true);
      markAsViewed(randomName.id);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // Пагинация для всех имен
  const totalPages = Math.ceil(filteredNames.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNames = filteredNames.slice(startIndex, endIndex);

  if (error) return <Alert severity="error">{error}<Button onClick={()=>void loadAllahNames()}>Повторить загрузку</Button></Alert>;
  if (!isLoading && allahNames.length === 0) return <Alert severity="info">Имена пока не загружены на сервер.<Button onClick={()=>void loadAllahNames()}>Обновить</Button></Alert>;
  if (isLoading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Имена Аллаха
          </Typography>
          <Alert severity="info">
            Загружаем имена Аллаха...
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {allahNames.some(name=>name.metadata?.source_project==='my-prayers/muslim-data-android')&&<Alert severity="info" sx={{mb:2}}>Источник: <a href="https://github.com/my-prayers/muslim-data-android/tree/f7968b8765bfa23706751edadc3fed2fa17a9864" target="_blank" rel="noreferrer">MuslimData</a>, Apache-2.0. Русский перевод и английское значение из этого каталога. Аудиозаписи и ссылки на аяты для этих записей пока недоступны.</Alert>}
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Прогресс" />
          <Tab label="Все имена" />
          <Tab label="Изученные" />
        </Tabs>

        {/* Поисковая строка - только на вкладке "Все имена" */}
        {selectedTab === 1 && (
          <TextField
            fullWidth
            placeholder={`Поиск по имени, описанию, на арабском или русском... (найдено: ${filteredNames.length} из 99)`}
            value={searchQuery}
            onChange={(e) => {
              const query = e.target.value;
              // Очищаем предыдущий таймер
              if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
              }
              // Устанавливаем новый таймер для поиска с меньшей задержкой
              searchTimeoutRef.current = setTimeout(() => {
                searchNames(query);
              }, 150);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? <CircularProgress size={20} /> : <Search />}
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
        )}

        {/* Вкладка прогресса */}
        {selectedTab === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Ваш прогресс изучения
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Прогресс: {progress.progress_percent}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.progress_percent}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <School color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Изучено</Typography>
                  <Typography variant="h4" color="primary">
                    {progress.learned_names}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    из {progress.total_names}
                  </Typography>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Star color="warning" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Просмотрено</Typography>
                  <Typography variant="h4" color="warning">
                    {progress.viewed_names}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    раз
                  </Typography>
                </Card>
              </Box>
            </Box>

            {currentName && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Следующее имя для изучения
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60 }}>
                      <Typography variant="h4">
                        {currentName.name_arabic}
                      </Typography>
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {currentName.name_latin || currentName.name_ascii || 'Имя'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentName.name_cyr || currentName.name_latin || 'Транслитерация не опубликована'}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {currentName.translations?.[0] || currentName.properties?.meaning_detailed || 'Значение'}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleNameClick(currentName)}
                    >
                      Изучить
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleRandomName}
                    >
                      Случайное имя
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* Вкладка всех имен */}
        {selectedTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              {currentNames.map((name) => (
                <Card
                  key={name.id}
                  sx={{
                    flex: '1 1 300px',
                    minWidth: '300px',
                    cursor: 'pointer',
                    opacity: name.is_learned ? 1 : 0.8,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                  onClick={() => handleNameClick(name)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: name.is_learned ? 'success.main' : 'primary.main' }}>
                        <Typography variant="h6">
                          {name.name_arabic}
                        </Typography>
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {name.name_latin || name.name_ascii || 'Имя'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {name.name_cyr || name.name_latin || 'Транслитерация не опубликована'}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {name.translations?.[0] || name.properties?.meaning_detailed || 'Значение'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {name.is_learned && (
                        <Chip
                          label="Изучено"
                          color="success"
                          size="small"
                          icon={<CheckCircle />}
                        />
                      )}
                      <Chip
                        label={`Просмотрено: ${name.times_viewed}`}
                        size="small"
                        icon={<Star />}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Пагинация */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </Box>
        )}

        {/* Вкладка изученных имен */}
        {selectedTab === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Изученные имена
            </Typography>

            {allahNames.filter(name => name.is_learned).length === 0 ? (
              <Alert severity="info">
                Вы еще не изучили ни одного имени. Начните изучение!
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {allahNames
                  .filter(name => name.is_learned)
                  .map((name) => (
                    <Card
                      key={name.id}
                      sx={{
                        flex: '1 1 300px',
                        minWidth: '300px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        }
                      }}
                      onClick={() => handleNameClick(name)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'success.main' }}>
                            <Typography variant="h6">
                              {name.name_arabic}
                            </Typography>
                          </Avatar>
                          <Box>
                            <Typography variant="h6">
                              {name.name_latin || name.name_ascii || 'Имя'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {name.name_cyr || name.name_latin || 'Транслитерация не опубликована'}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {name.translations?.[0] || name.properties?.meaning_detailed || 'Значение'}
                        </Typography>

                        <Chip
                          label={`Изучено ${name.learned_at ? new Date(name.learned_at).toLocaleDateString() : ''}`}
                          color="success"
                          size="small"
                          icon={<CheckCircle />}
                        />
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            )}
          </Box>
        )}

        {/* Диалог с деталями имени */}
        <Dialog
          open={showNameDialog}
          onClose={() => setShowNameDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedName?.name_latin || selectedName?.name_ascii || 'Имя Аллаха'}
          </DialogTitle>
          <DialogContent>
            {selectedName && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80 }}>
                    <Typography variant="h3">
                      {selectedName.name_arabic}
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="h5">
                      {selectedName.name_latin || selectedName.name_ascii || 'Имя'}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {selectedName.name_cyr || selectedName.name_latin || 'Транслитерация не опубликована'}
                    </Typography>
                  </Box>
                </Box>

                {/* Основные переводы */}
                {selectedName.translations && selectedName.translations.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Переводы:
                    </Typography>
                    {selectedName.translations.map((translation: string, index: number) => (
                      <Typography key={index} variant="body1" sx={{ mb: 0.5 }}>
                        • {translation}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Описание */}
                {selectedName.notes && selectedName.notes.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Описание:
                    </Typography>
                    {selectedName.notes.map((note: string, index: number) => (
                      <Typography key={index} variant="body1" sx={{ mb: 1, textAlign: 'justify' }}>
                        {note}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Подробное значение */}
                {selectedName.properties?.meaning_detailed && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Подробное значение:
                    </Typography>
                    <Typography variant="body1">
                      {selectedName.properties.meaning_detailed}
                    </Typography>
                  </Box>
                )}

                {/* Лингвистический корень */}
                {selectedName.properties?.linguistic_root && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Лингвистический корень:
                    </Typography>
                    <Typography variant="body1">
                      {selectedName.properties.linguistic_root}
                    </Typography>
                  </Box>
                )}

                {/* Польза */}
                {selectedName.properties?.benefits && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Польза:
                    </Typography>
                    <Typography variant="body1">
                      {selectedName.properties.benefits}
                    </Typography>
                  </Box>
                )}

                {/* Когда использовать */}
                {selectedName.properties?.when_to_use && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Когда использовать:
                    </Typography>
                    <Typography variant="body1">
                      {selectedName.properties.when_to_use}
                    </Typography>
                  </Box>
                )}

                {/* Примеры дуа */}
                {selectedName.properties?.dua_examples && selectedName.properties.dua_examples.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Примеры дуа:
                    </Typography>
                    {selectedName.properties.dua_examples.map((dua: string, index: number) => (
                      <Typography key={index} variant="body1" sx={{ mb: 0.5, fontStyle: 'italic' }}>
                        "{dua}"
                      </Typography>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Аяты Корана */}
                {selectedName.quran_references && selectedName.quran_references.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Упоминается в аятах:
                    </Typography>
                    {selectedName.quran_references.map((ref: any, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 1,
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'primary.50',
                            borderColor: 'primary.main',
                            transform: 'translateY(-1px)',
                            boxShadow: 1
                          }
                        }}
                        onClick={() => handleAyahClick(ref)}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          Сура {ref.surah}, аят {ref.ayah_start}
                          {ref.ayah_end && ref.ayah_end !== ref.ayah_start && `-${ref.ayah_end}`}
                        </Typography>
                        {ref.reference_text && (
                          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                            "{ref.reference_text}"
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          Нажмите для просмотра аята
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Аудио */}
                {selectedName.audio && (
                  <Box sx={{ mb: 3 }}>
                    <AudioPlayer
                      src={'/api/v1/allah-names/audio/'+encodeURIComponent(selectedName.audio.filename)}
                      token={token}
                      title="Прослушать произношение имени"
                    />
                    {selectedName.audio.reciter_name && (
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                        Чтец: {selectedName.audio.reciter_name}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Просмотрено: ${selectedName.times_viewed} раз`}
                    size="small"
                    icon={<Star />}
                  />
                  {selectedName.is_learned && (
                    <Chip
                      label="Изучено"
                      color="success"
                      size="small"
                      icon={<CheckCircle />}
                    />
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowNameDialog(false)}>
              Закрыть
            </Button>
            {selectedName && !selectedName.is_learned && (
              <Button
                variant="contained"
                onClick={() => handleLearnName(selectedName.id)}
                startIcon={<CheckCircle />}
              >
                Отметить как изученное
              </Button>
            )}
            {selectedName && selectedName.is_learned && (
              <Button
                variant="outlined"
                color="warning"
                onClick={async () => {
                  await unmarkAsLearned(selectedName.id);
                }}
              >
                Снять отметку «изучено»
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleNextName}
              startIcon={<Refresh />}
            >
              Следующее имя
            </Button>
          </DialogActions>
        </Dialog>

        {/* Компонент для просмотра аята */}
        {selectedAyah && (
          <AyahViewer
            open={showAyahDialog}
            onClose={() => setShowAyahDialog(false)}
            surah={selectedAyah.surah}
            ayah_start={selectedAyah.ayah_start}
            ayah_end={selectedAyah.ayah_end}
            onBackToName={handleBackToName}
            token={token || undefined}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AllahNamesProgress;
